import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Row,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Typography,
  type TableColumnsType,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { ListenerName, formatHandle } from '../../lib/listenerName';
import { formatDuration, formatWhen, type StatsRangeParams } from '../listeners/listenerStats';
import { StatsRangeFilter, useUrlStatsRange } from '../listeners/ListenerStatsWidgets';
import {
  tempTrackingCsv,
  type TempTrackingReport,
  type TempTrackingRow,
  type TempTrackingSession,
} from './tempTracking';

/**
 * TEMPORARY TRACKING — listeners' online time, including the time the regular
 * history MISSED: an older app build showed them Online while the server held
 * them OFFLINE (not callable). For compensating that time. Remove once the old
 * builds are gone.
 */
export function TempTrackingPage() {
  const { preset, setPreset, setCustom, range } = useUrlStatsRange('today');
  const [onlyMissed, setOnlyMissed] = useState(true);

  const report = useQuery({
    queryKey: ['admin', 'temp-tracking', range],
    queryFn: async () =>
      (await api.get<TempTrackingReport>('/admin/listeners/temp-tracking', { params: range })).data,
    enabled: range !== null,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });

  const rows = useMemo(() => {
    const all = report.data?.rows ?? [];
    return onlyMissed ? all.filter((r) => r.missedOnlineSeconds > 0) : all;
  }, [report.data, onlyMissed]);

  const totals = useMemo(() => {
    const all = report.data?.rows ?? [];
    return {
      listenersWithMissed: all.filter((r) => r.missedOnlineSeconds > 0).length,
      missedSeconds: all.reduce((n, r) => n + r.missedOnlineSeconds, 0),
      missingNow: all.filter((r) => r.now === 'missing').length,
    };
  }, [report.data]);

  function downloadCsv() {
    const data = report.data;
    if (!data) return;
    const csv = tempTrackingCsv({ ...data, rows });
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `temporary-tracking_${data.range.from.slice(0, 10)}_to_${data.range.to.slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const columns: TableColumnsType<TempTrackingRow> = [
    {
      title: 'Listener',
      render: (_: unknown, r) => (
        <Space direction="vertical" size={2}>
          <ListenerName
            listener={{ displayName: r.displayName, handle: r.handle, id: r.id }}
            listenerId={r.id}
            tab="online"
            secondary="none"
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {[formatHandle(r.handle), `••${r.phoneLast4}`].filter(Boolean).join(' · ')}
            {r.isSuspended ? ' · suspended' : ''}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Now',
      width: 190,
      render: (_: unknown, r) =>
        r.now === 'online' ? (
          <Tag color="green">Online</Tag>
        ) : r.now === 'missing' ? (
          <Tag color="orange">App shows Online — not callable</Tag>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Recorded online',
      align: 'right',
      sorter: (a, b) => a.recordedOnlineSeconds - b.recordedOnlineSeconds,
      render: (_: unknown, r) => formatDuration(r.recordedOnlineSeconds),
    },
    {
      title: 'Missed online',
      align: 'right',
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.missedOnlineSeconds - b.missedOnlineSeconds,
      render: (_: unknown, r) =>
        r.missedOnlineSeconds > 0 ? (
          <Typography.Text strong type="danger">
            {formatDuration(r.missedOnlineSeconds)}
          </Typography.Text>
        ) : (
          '—'
        ),
    },
    {
      title: 'Total online',
      align: 'right',
      sorter: (a, b) => a.totalOnlineSeconds - b.totalOnlineSeconds,
      render: (_: unknown, r) => <b>{formatDuration(r.totalOnlineSeconds)}</b>,
    },
    {
      title: 'Missed sessions',
      align: 'right',
      dataIndex: 'missedSessions',
    },
    {
      title: 'Last missed',
      render: (_: unknown, r) => (r.lastMissedAt ? formatWhen(r.lastMissedAt) : '—'),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Online-time compensation
        </Typography.Title>
        <Space wrap>
          <StatsRangeFilter preset={preset} setPreset={setPreset} setCustom={setCustom} />
          <Button icon={<DownloadOutlined />} onClick={downloadCsv} disabled={!report.data || rows.length === 0}>
            Download CSV
          </Button>
        </Space>
      </Space>

      <Alert
        type="info"
        showIcon
        message="Online time missed by older app builds"
        description={
          <>
            <b>Missed online</b> is time when a listener on an older app (2.0.15 or earlier) saw
            themselves as Online, but the server had them Offline — their one &ldquo;go online&rdquo;
            request at app start failed, or a logout elsewhere switched them off. Callers could not
            reach them then, and it is not in the regular online history. <b>Total online</b> merges
            both, so overlapping time is counted once.{' '}
            {report.data?.trackingStartedAt
              ? `Tracking started ${formatWhen(report.data.trackingStartedAt)}; earlier missed time was never recorded and cannot be shown.`
              : 'No missed time has been recorded yet; earlier missed time was never recorded and cannot be shown.'}
          </>
        }
      />

      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Listeners with missed time" value={totals.listenersWithMissed} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Total missed online time" value={formatDuration(totals.missedSeconds)} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="App shows Online but not callable — now" value={totals.missingNow} />
          </Card>
        </Col>
      </Row>

      <Card
        extra={
          <Space>
            <Typography.Text>Only listeners with missed time</Typography.Text>
            <Switch checked={onlyMissed} onChange={setOnlyMissed} />
          </Space>
        }
      >
        <Table<TempTrackingRow>
          rowKey="id"
          loading={report.isFetching && !report.data}
          dataSource={rows}
          columns={columns}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          locale={{ emptyText: <Empty description="No online time in this range" /> }}
          expandable={{
            rowExpandable: (r) => r.missedSessions > 0,
            expandedRowRender: (r) => <MissedSessions listenerId={r.id} range={range} />,
          }}
        />
      </Card>
    </Space>
  );
}

function MissedSessions({ listenerId, range }: { listenerId: string; range: StatsRangeParams | null }) {
  const sessions = useQuery({
    queryKey: ['admin', 'temp-tracking', listenerId, range],
    queryFn: async () =>
      (
        await api.get<{ sessions: TempTrackingSession[]; truncated: boolean }>(
          `/admin/listeners/${listenerId}/temp-tracking`,
          { params: range },
        )
      ).data,
    enabled: range !== null,
  });
  return (
    <Table<TempTrackingSession>
      size="small"
      rowKey="startedAt"
      loading={sessions.isLoading}
      dataSource={sessions.data?.sessions ?? []}
      pagination={false}
      columns={[
        { title: 'From', render: (_: unknown, s) => formatWhen(s.startedAt) },
        { title: 'Last seen active', render: (_: unknown, s) => formatWhen(s.lastSeenAt) },
        { title: 'Duration', align: 'right', render: (_: unknown, s) => formatDuration(s.durationSeconds) },
        { title: 'Activity signals', align: 'right', dataIndex: 'beats' },
        { title: 'App was', render: (_: unknown, s) => s.lastAppState ?? '—' },
      ]}
      footer={sessions.data?.truncated ? () => 'Showing the newest 2,000 sessions.' : undefined}
    />
  );
}
